import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Sparkles, ArrowRight, Pin, PinOff } from "lucide-react";

/**
 * ChapterEchoes — a warm "you've seen this idea before" whisper at the
 * bottom of a chapter reader.  Fetches /api/chapters/:slug/echoes for
 * the current chapter; renders in-title echoes (echoes) plus any
 * previously-pinned concepts that still have cross-world overlap
 * (pinned_only).  Silent-fail (nothing) when anon or no overlap.
 */
export default function ChapterEchoes({ slug }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState({});  // term → busy flag

  const refresh = () => {
    if (!slug) return;
    api.get(`/chapters/${slug}/echoes`)
      .then(({ data }) => setData(data))
      .catch(() => setData({ echoes: [], pinned_only: [] }));
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [slug]);

  const togglePin = async (term, isPinned) => {
    setBusy((b) => ({ ...b, [term]: true }));
    try {
      if (isPinned) {
        await api.delete(`/echoes/pin/${encodeURIComponent(term)}`);
        toast.success(`Unpinned "${term}".`);
      } else {
        await api.post(`/echoes/pin`, { term });
        toast.success(`Pinned "${term}" — it'll surface on every chapter that touches it.`);
      }
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't toggle pin.");
    } finally {
      setBusy((b) => ({ ...b, [term]: false }));
    }
  };

  if (!data || (!data.echoes?.length && !data.pinned_only?.length)) return null;

  const renderRow = (e) => (
    <li key={e.term}>
      <div className="mb-2 text-sm flex items-center gap-2 flex-wrap">
        <span className="font-heading text-lg font-semibold text-ink capitalize">{e.term}</span>
        <span className="text-[10px] font-mono text-ink2/70 uppercase tracking-widest">
          · seen in {e.worlds.length} other world{e.worlds.length !== 1 ? "s" : ""}
        </span>
        <button
          onClick={() => togglePin(e.term, e.is_pinned)}
          disabled={busy[e.term]}
          className={`ml-auto text-[10px] uppercase tracking-widest font-bold inline-flex items-center gap-1 px-2 py-1 rounded-full border transition ${
            e.is_pinned
              ? "bg-marigold text-cream border-marigold hover:bg-marigold/80"
              : "border-ink/30 text-ink2 hover:border-marigold hover:text-marigold"
          }`}
          data-testid={`chapter-echo-pin-${e.term}`}
        >
          {e.is_pinned ? <><PinOff className="w-3 h-3" /> pinned</> : <><Pin className="w-3 h-3" /> pin</>}
        </button>
      </div>
      <ul className="flex flex-col gap-1.5 pl-3 border-l-2 border-marigold/40">
        {e.worlds.map((w) => (
          <li key={w.slug}>
            <Link
              to={`/library/${w.example.slug}`}
              className="group inline-flex items-center gap-2 text-sm hover:text-marigold transition"
              data-testid={`chapter-echo-${e.term}-${w.slug}`}
            >
              <span className="text-[9px] font-mono font-bold text-marigold uppercase tracking-[0.22em] w-16 flex-shrink-0">{w.kicker}</span>
              <span className="text-[10px] font-mono text-ink/50">{w.example.code}</span>
              <span className="font-heading font-semibold text-ink group-hover:text-marigold transition">{w.example.title}</span>
              <ArrowRight className="w-3.5 h-3.5 text-ink/40 group-hover:text-marigold group-hover:translate-x-0.5 transition-transform" />
            </Link>
          </li>
        ))}
      </ul>
    </li>
  );

  return (
    <section
      className="mt-12 rounded-3xl border-2 border-marigold/40 bg-marigold/[0.06] p-6 md:p-7"
      data-testid={`chapter-echoes-${slug}`}
    >
      {/* Pinned-only echoes at the top (concepts learner explicitly pinned
          earlier that also have overlap here, even if not in this title) */}
      {data.pinned_only?.length > 0 && (
        <div className="mb-6 pb-6 border-b border-marigold/30" data-testid={`chapter-echoes-pinned-${slug}`}>
          <div className="flex items-center gap-2 mb-3 text-[10px] uppercase tracking-[0.28em] font-bold text-marigold">
            <Pin className="w-3.5 h-3.5" /> Pinned concepts you're following
          </div>
          <ul className="space-y-4">{data.pinned_only.map(renderRow)}</ul>
        </div>
      )}

      {data.echoes?.length > 0 && (
        <>
          <div className="flex items-center gap-2 mb-3 text-[10px] uppercase tracking-[0.28em] font-bold text-marigold">
            <Sparkles className="w-3.5 h-3.5" /> You've seen this idea before
          </div>
          <p className="text-ink2 text-sm mb-5 italic max-w-2xl leading-relaxed">
            Some ideas walk with you across the book.  Pin any of them to keep
            them whispering on future chapters that touch the same idea.
          </p>
          <ul className="space-y-4" data-testid={`chapter-echoes-list-${slug}`}>
            {data.echoes.map(renderRow)}
          </ul>
        </>
      )}
    </section>
  );
}
